import { Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from "react-native";

import { webTapFloor, type UiTheme } from "../theme";

// Two or more tabs as one joined control (mockup .seg). On: the surface2
// cell, weight 700 and a short rule under the label, so the state is never
// the fill alone. Off while disabled: an opaque surface and ink2 text, not
// an opacity on the brand. Born in the diary (#368); #472 New program is
// the second caller.
export function SegmentedTabs<T extends string>({
  theme,
  options,
  selected,
  label,
  onChange,
  disabled = false,
  style,
}: {
  theme: UiTheme;
  options: readonly T[];
  selected: T;
  label: (option: T) => string;
  onChange: (option: T) => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const styles = makeStyles(theme);
  return (
    <View style={[styles.seg, style]} accessibilityRole="tablist">
      {options.map((o, i) => {
        const on = selected === o;
        return (
          <Pressable
            key={o}
            accessibilityRole="tab"
            accessibilityState={{ selected: on, disabled }}
            // RNW drops accessibilityState: these reach the web.
            aria-selected={on}
            aria-disabled={disabled}
            disabled={disabled}
            onPress={() => onChange(o)}
            style={[styles.cell, i > 0 && styles.divider, on && styles.on, disabled && styles.off]}
          >
            <View style={styles.labelBox}>
              <Text style={[styles.label, on && styles.labelOn]}>{label(o)}</Text>
              {on ? <View style={styles.rule} /> : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    seg: {
      flexDirection: "row",
      borderWidth: 1,
      borderColor: p.borderStrong,
      borderRadius: r.r2,
      overflow: "hidden",
      backgroundColor: p.surface,
    },
    cell: {
      flex: 1,
      minHeight: webTapFloor ?? 44,
      paddingHorizontal: s.s4,
      alignItems: "center",
      justifyContent: "center",
    },
    divider: { borderLeftWidth: 1, borderLeftColor: p.borderStrong },
    on: { backgroundColor: p.surface2 },
    // Opaque and legible: ink2 on surface/surface2, no opacity.
    off: { backgroundColor: p.surface },
    labelBox: { alignSelf: "stretch", alignItems: "center", paddingBottom: s.s1 },
    label: { textAlign: "center", color: p.ink2, fontSize: ty.sizes.base, fontWeight: "500" },
    labelOn: { color: p.ink, fontWeight: "700" },
    rule: { alignSelf: "stretch", height: 2, marginTop: s.s1, marginHorizontal: s.s3, backgroundColor: p.ink },
  });
}
